/**
 * Pilot Portal
 * Access point for pilots to sign in and access the job board
 */

import React, { useState } from 'react';
import './PilotPortal.css';

const PilotPortal = ({ apiBaseUrl, onPilotLoggedIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState('email'); // 'email' or 'code'
  const [success, setSuccess] = useState(null);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error('Login failed. Please check your credentials.');
      }

      const data = await response.json();
      const token = data.access_token;
      const user = { id: data.user_id, email };

      // Store token in localStorage
      localStorage.setItem('pilotToken', token);
      localStorage.setItem('pilotUser', JSON.stringify(user));

      onPilotLoggedIn(token, user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setPasswordMatch(false);
      setError('Passwords do not match');
      return;
    }
    
    setPasswordMatch(true);
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          phone,
          city,
          country,
          address_line1: addressLine1,
          address_line2: addressLine2,
          postal_code: postalCode,
        }),
      });

      if (!response.ok) {
        throw new Error('Sign up failed. Please try again.');
      }

      const data = await response.json();
      const token = data.access_token;
      const user = { id: data.user_id, email, first_name: firstName };

      localStorage.setItem('pilotToken', token);
      localStorage.setItem('pilotUser', JSON.stringify(user));

      onPilotLoggedIn(token, user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordStart = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${apiBaseUrl}/auth/password-reset/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Email not found. Please check and try again.');
      }

      setSuccess('✅ Code sent to your email! Enter the code below.');
      setResetStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordConfirm = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setPasswordMatch(false);
      setError('Passwords do not match');
      return;
    }

    setPasswordMatch(true);
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${apiBaseUrl}/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          code: resetCode,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        throw new Error('Invalid code or reset failed. Please try again.');
      }

      setSuccess('✅ Password reset successful! You can now log in.');
      setTimeout(() => {
        setIsForgotPassword(false);
        setResetStep('email');
        setEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pilot-portal">
      <div className="portal-container">
        <div className="portal-card">
          <div className="portal-header">
            <h1>🚗 ErrandBridge Pilot</h1>
            <p>Earn money running errands for customers</p>
          </div>

          {error && (
            <div className="error-banner">
              <span>❌ {error}</span>
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}

          {success && (
            <div className="success-banner">
              <span>{success}</span>
              <button onClick={() => setSuccess(null)}>✕</button>
            </div>
          )}

          {isForgotPassword ? (
            <form onSubmit={resetStep === 'email' ? handleForgotPasswordStart : handleForgotPasswordConfirm} className="auth-form">
              <h2>🔐 Reset Password</h2>

              {resetStep === 'email' ? (
                <>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="pilot@example.com"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading}
                  >
                    {loading ? '⏳ Sending Code...' : '📧 Send Reset Code'}
                  </button>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Reset Code</label>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      placeholder="Enter 6-digit code from email"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (e.target.value && newPassword && e.target.value !== newPassword) {
                          setPasswordMatch(false);
                        } else {
                          setPasswordMatch(true);
                        }
                      }}
                      placeholder="••••••••"
                      required
                    />
                    {!passwordMatch && (
                      <small style={{ color: '#dc2626', marginTop: '4px', display: 'block' }}>
                        ❌ Passwords do not match
                      </small>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading || !passwordMatch}
                  >
                    {loading ? '⏳ Resetting...' : '🔑 Reset Password'}
                  </button>
                </>
              )}

              <p className="toggle-auth">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setResetStep('email');
                    setError(null);
                    setSuccess(null);
                  }}
                  className="link-btn"
                >
                  Sign In
                </button>
              </p>
            </form>
          ) : isSignUp ? (
            <form onSubmit={handleSignUp} className="auth-form">
              <h2>Create Pilot Account</h2>

              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="pilot@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+234 800 000 0000"
                  required
                />
              </div>

              <div className="form-group">
                <label>Address Line 1</label>
                <input
                  type="text"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="123 Main Street"
                  required
                />
              </div>

              <div className="form-group">
                <label>Address Line 2 (Optional)</label>
                <input
                  type="text"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Apartment, suite, etc."
                />
              </div>

              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Lagos"
                  required
                />
              </div>

              <div className="form-group">
                <label>Country</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                >
                  <option value="">Select Country</option>
                  <option value="United Kingdom">🇬🇧 United Kingdom</option>
                  <option value="United States">🇺🇸 United States</option>
                  <option value="Nigeria">🇳🇬 Nigeria</option>
                  <option value="Canada">🇨🇦 Canada</option>
                  <option value="Germany">🇩🇪 Germany</option>
                  <option value="France">🇫🇷 France</option>
                  <option value="India">🇮🇳 India</option>
                  <option value="South Africa">🇿🇦 South Africa</option>
                </select>
              </div>

              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="10001"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#666',
                      fontSize: '14px'
                    }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (e.target.value && password && e.target.value !== password) {
                      setPasswordMatch(false);
                    } else {
                      setPasswordMatch(true);
                    }
                  }}
                  placeholder="••••••••"
                  required
                />
                {!passwordMatch && (
                  <small style={{ color: '#dc2626', marginTop: '4px', display: 'block' }}>
                    ❌ Passwords do not match
                  </small>
                )}
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={loading || !passwordMatch}
              >
                {loading ? '⏳ Creating Account...' : '✅ Create Account'}
              </button>

              <p className="toggle-auth">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setIsSignUp(false)}
                  className="link-btn"
                >
                  Sign In
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleSignIn} className="auth-form">
              <h2>Pilot Sign In</h2>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="pilot@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
              >
                {loading ? '⏳ Signing In...' : '🚀 Start Earning'}
              </button>

              <p className="toggle-auth">
                Forgot your password?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(true);
                    setError(null);
                  }}
                  className="link-btn"
                >
                  Reset It
                </button>
              </p>

              <p className="toggle-auth">
                New to ErrandBridge?{' '}
                <button
                  type="button"
                  onClick={() => setIsSignUp(true)}
                  className="link-btn"
                >
                  Create Account
                </button>
              </p>
            </form>
          )}

          <div className="portal-features">
            <div className="feature">
              <span className="icon">💰</span>
              <p><strong>Earn Money</strong> - Get paid per errand</p>
            </div>
            <div className="feature">
              <span className="icon">🗺️</span>
              <p><strong>Easy Routes</strong> - GPS tracks your journey</p>
            </div>
            <div className="feature">
              <span className="icon">⭐</span>
              <p><strong>Build Rating</strong> - Earn customer reviews</p>
            </div>
            <div className="feature">
              <span className="icon">📱</span>
              <p><strong>Mobile Friendly</strong> - Works on all devices</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PilotPortal;
